import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Server } from 'lucide-react';
import { useUpload } from '../contexts/UploadContext';
import { toast } from 'react-toastify';

/**
 * Component that detects potentially stuck frontend states and provides
 * a user-friendly way to clear them. This solves issues like:
 * "Generating clip 3/6... Creating clip 4/6" showing from a week ago
 */
const StuckStateDetector: React.FC = () => {
  const { tasks, clearAllStuckStates } = useUpload();
  const [showStuckWarning, setShowStuckWarning] = useState(false);
  const [stuckTasks, setStuckTasks] = useState<any[]>([]);
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    const checkForStuckStates = () => {
      const now = Date.now();
      const potentiallyStuckTasks = tasks.filter(task => {
        const taskAge = now - task.startTime.getTime();
        
        // Tasks stuck for more than 30 minutes in analyzing state
        // or any task older than 6 hours
        return (
          (task.status === 'analyzing' && taskAge > 1800000) || // 30 minutes
          (taskAge > 21600000) // 6 hours
        );
      });

      // Also check localStorage for old entries that might cause issues
      let hasOldLocalStorageEntries = false;
      try {
        Object.keys(localStorage).forEach(key => {
          if (key.includes('synthetic_progress') || 
              key.includes('upload_tasks') ||
              key.includes('processing_videos')) {
            const data = localStorage.getItem(key);
            if (data) {
              try {
                const parsed = JSON.parse(data);
                // Check if it's an array with old timestamps or has old timestamp directly
                if (Array.isArray(parsed)) {
                  const hasOldTasks = parsed.some((item: any) => {
                    if (item.startTime) {
                      const taskAge = now - new Date(item.startTime).getTime();
                      return taskAge > 21600000; // 6 hours
                    }
                    return false;
                  });
                  if (hasOldTasks) hasOldLocalStorageEntries = true;
                } else if (parsed.timestamp && (now - parsed.timestamp) > 21600000) {
                  hasOldLocalStorageEntries = true;
                }
              } catch (e) {
                // If parsing fails, it might be corrupted
                hasOldLocalStorageEntries = true;
              }
            }
          }
        });
      } catch (e) {
        console.warn('Error checking localStorage for stuck states:', e);
      }

      setStuckTasks(potentiallyStuckTasks);
      setShowStuckWarning(potentiallyStuckTasks.length > 0 || hasOldLocalStorageEntries);
    };

    // Check immediately and then every 30 seconds
    checkForStuckStates();
    const interval = setInterval(checkForStuckStates, 30000);

    return () => clearInterval(interval);
  }, [tasks]);

  const handleBackendReset = async () => {
    setIsResetting(true);
    try {
      const response = await fetch('/api/v1/admin/clear-stuck-videos', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${await getCurrentUserToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Reset ${data.reset_count} stuck videos from backend`);
        
        // Also clear frontend state
        clearAllStuckStates();
      } else {
        throw new Error(`Backend reset failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Backend reset failed:', error);
      toast.error('Backend reset failed, clearing frontend state only');
      // Fallback to frontend-only reset
      clearAllStuckStates();
    } finally {
      setIsResetting(false);
    }
  };

  const getCurrentUserToken = async () => {
    // Import auth here to avoid circular dependencies
    const { auth } = await import('../lib/firebase');
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
    throw new Error('No authenticated user');
  };

  if (!showStuckWarning) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      <div className="bg-white border border-primary-200 rounded-xl p-6 shadow-lifted">
        <div className="flex items-start">
          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 mr-4">
            <AlertTriangle className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-black mb-2">
              Stuck Processing Detected
            </h3>
            <p className="text-xs text-primary-600 mb-4 leading-relaxed">
              {stuckTasks.length > 0 ? (
                <>Found {stuckTasks.length} task{stuckTasks.length !== 1 ? 's' : ''} that may be stuck.</>
              ) : (
                <>Old processing data detected that may cause display issues.</>
              )}
              {' '}This might show outdated progress like "Generating clip 3/6..." from previous sessions.
            </p>
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <button
                  onClick={clearAllStuckStates}
                  disabled={isResetting}
                  className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-black rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="h-3 w-3 mr-1.5" />
                  Clear Frontend
                </button>
                <button
                  onClick={handleBackendReset}
                  disabled={isResetting}
                  className="inline-flex items-center px-3 py-2 text-xs font-medium text-black bg-white border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Server className={`h-3 w-3 mr-1.5 ${isResetting ? 'animate-spin' : ''}`} />
                  Reset Backend
                </button>
              </div>
              <button
                onClick={() => setShowStuckWarning(false)}
                className="inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-primary-600 bg-transparent border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors w-full"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StuckStateDetector;
